import {
  addMonths,
  monthOf,
  parseAmount,
  type AccountKind,
  type InterestMethod,
  type LoanInput,
} from '@koverta/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '../components/toast';
import { Dialog, Flag, MoneyInput, Seg } from '../components/ui';
import { errorMessage, post } from '../lib/api';
import { useHousehold } from '../lib/household';
import { invalidateScopes, useAccounts, usePlan } from '../lib/queries';
import { parsePercent } from '../lib/loan';

export function AddAccountDialog({ onClose }: { onClose: () => void }) {
  const { fmt, today } = useHousehold();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [currency, setCurrency] = useState<'RSD' | 'EUR'>(fmt.base);
  const [kind, setKind] = useState<AccountKind>('checking');
  const [balance, setBalance] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const tracking = kind === 'asset' || kind === 'liability' || kind === 'loan';
  const loan = useLoanFields(currency);
  const save = async () => {
    if (kind === 'loan') {
      if (!name.trim()) return setErr('Name the loan');
      const input = loan.input(name, institution);
      if (typeof input === 'string') return setErr(input);
      try {
        const { id } = await post<{ id: string }>('/api/loans', input);
        invalidateScopes(['accounts', 'plan', 'transactions']);
        toast(`Added ${name}`);
        onClose();
        navigate(`/accounts/${id}`);
      } catch (e) {
        setErr(errorMessage(e));
      }
      return;
    }
    if (!name.trim()) return setErr('Name the account');
    const startingBalance = balance.trim() ? parseAmount(balance, fmt.locale) : 0;
    if (startingBalance === null) return setErr('Enter the balance as a number');
    try {
      const { id } = await post<{ id: string }>('/api/accounts', {
        name,
        institution,
        currency,
        kind,
        startingBalance: kind === 'liability' ? -Math.abs(startingBalance) : startingBalance,
        startingDate: today,
      });
      invalidateScopes(['accounts', 'plan', 'transactions']);
      toast(`Added ${name}`);
      onClose();
      navigate(`/accounts/${id}`);
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <Dialog
      title={kind === 'loan' ? 'Add loan' : 'Add account'}
      onClose={onClose}
      size="md"
      footer={
        <>
          <span className="sp" />
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            {kind === 'loan' ? 'Add loan' : 'Add account'}
          </button>
        </>
      }
    >
      <div className="fgrid">
        <label className="field">
          Name
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={kind === 'loan' ? 'Car loan' : 'Tekući RSD'}
            maxLength={60}
          />
        </label>
        <label className="field">
          Bank / institution
          <input
            className="input"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="Raiffeisen"
            maxLength={60}
          />
        </label>
        <div className="field">
          Currency
          <Seg
            value={currency}
            onChange={setCurrency}
            label="Currency"
            options={[
              [
                'RSD',
                <>
                  <Flag currency="RSD" /> RSD
                </>,
              ],
              [
                'EUR',
                <>
                  <Flag currency="EUR" /> EUR
                </>,
              ],
            ]}
          />
        </div>
        <label className="field">
          Type
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value as AccountKind)}>
            <option value="checking">Checking (budget)</option>
            <option value="savings">Savings (budget)</option>
            <option value="cash">Cash (budget)</option>
            <option value="credit">Credit card (budget)</option>
            <option value="asset">Asset (tracking)</option>
            <option value="loan">Loan with installments (tracking)</option>
            <option value="liability">Other debt (tracking)</option>
          </select>
        </label>
        {kind === 'loan' ? loan.fields : null}
        {kind !== 'loan' ? (
          <label className="field wide">
            Current balance ({currency})
            <MoneyInput
              className="input r"

              value={balance}
              onChange={setBalance}
            />
          </label>
        ) : null}
      </div>
      <div className="muted" style={{ fontSize: 12.5 }}>
        {kind === 'loan'
          ? 'The loan is a tracking account: it lowers net worth, not the budget. Each installment is paid from the category, and the interest is entered on the loan every month.'
          : tracking
            ? 'Tracking accounts count towards net worth but stay outside the budget.'
            : `The starting balance goes to Ready to Assign${currency !== fmt.base ? `, converted to ${fmt.base} at today's NBS rate` : ''}.`}
      </div>
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}

/** Loan setup fields (loans.md §1–2): terms, where it's paid from and the payment category. */
function useLoanFields(currency: 'RSD' | 'EUR') {
  const { fmt, today } = useHousehold();
  const accounts = (useAccounts().data ?? []).filter((a) => a.onBudget && !a.closed);
  const plan = usePlan(monthOf(today)).data;
  const categories = (plan?.groups ?? []).flatMap((g) =>
    g.categories.filter((c) => !c.hidden).map((c) => ({ id: c.id, name: `${g.name} › ${c.name}` })),
  );
  const [owed, setOwed] = useState('');
  const [asOf, setAsOf] = useState(today);
  const [rate, setRate] = useState('');
  const [installment, setInstallment] = useState('');
  const [first, setFirst] = useState(`${addMonths(monthOf(today), 1)}-15`);
  const [from, setFrom] = useState('');
  const [category, setCategory] = useState('');
  const [method, setMethod] = useState<InterestMethod>('30_360');
  const fromId = from || accounts[0]?.id || '';

  const input = (name: string, institution: string): LoanInput | string => {
    const balance = parseAmount(owed, fmt.locale);
    if (!balance || balance <= 0) return 'Enter what is still owed';
    const annualRate = parsePercent(rate, fmt);
    if (annualRate === null) return 'Enter the annual interest rate, e.g. 6,25';
    const inst = parseAmount(installment, fmt.locale);
    if (!inst || inst <= 0) return 'Enter the monthly installment';
    if (!fromId) return 'Add a budget account to pay the loan from first';
    if (first < asOf) return 'The next installment is before the balance date';
    return {
      name,
      institution,
      currency,
      balance: Math.abs(balance),
      balanceDate: asOf,
      annualRate,
      installment: inst,
      firstPayment: first,
      fromAccountId: fromId,
      categoryId: category || null,
      interestMethod: method,
    };
  };

  const fields = (
    <>
      <label className="field">
        Still owed ({currency})
        <MoneyInput
          className="input r"

          value={owed}
          onChange={setOwed}
        />
      </label>
      <label className="field">
        As of
        <input className="input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </label>
      <label className="field">
        Annual interest rate (%)
        <input
          className="input r"
          inputMode="decimal"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          placeholder="6,25"
        />
      </label>
      <label className="field">
        Interest
        <select
          className="select"
          value={method}
          onChange={(e) => setMethod(e.target.value as InterestMethod)}
        >
          <option value="30_360">Monthly (30/360)</option>
          <option value="actual_365">Daily (actual/365)</option>
        </select>
      </label>
      <label className="field">
        Monthly installment ({currency})
        <MoneyInput
          className="input r"

          value={installment}
          onChange={setInstallment}
        />
      </label>
      <label className="field">
        Next installment
        <input className="input" type="date" value={first} onChange={(e) => setFirst(e.target.value)} />
      </label>
      <label className="field">
        Pay from
        <select className="select" value={fromId} onChange={(e) => setFrom(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.currency})
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        Payment category
        <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">New category named after the loan</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
    </>
  );
  return { fields, input };
}
